"use client"

import { useEffect, useState, useRef } from "react"
import { AnimatePresence, motion } from "framer-motion"
import Image from "next/image"
import Link from "next/link"
import { Home, Layers, Calculator, Menu, X, Info, FolderGit2, Tag, Mail } from "lucide-react"
import { Button } from "@/components/ui/button"
import { navLinks } from "@/features/home/data"
import { useHaptic } from "@/hooks/use-haptic"

const iconMap: Record<string, React.ElementType> = {
  Info,
  Layers,
  FolderGit2,
  Tag,
  Mail,
}

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeSection, setActiveSection] = useState("")
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(false)
  const isScrolling = useRef(false)
  const haptic = useHaptic()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
      
      // Sayfanın en altına gelinip gelinmediğini kontrol et (50px pay)
      const scrollPosition = window.innerHeight + window.scrollY
      const documentHeight = document.documentElement.scrollHeight
      setIsAtBottom(scrollPosition >= documentHeight - 50)
    }
    
    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    // Top bar ve bottom bar'daki tüm ID'leri birleştir
    const sectionIds = Array.from(new Set([
      ...navLinks.map((link) => link.href.replace("#", "")),
      "hero",
      "hesaplayici"
    ]))
    const observers: IntersectionObserver[] = []

    sectionIds.forEach((id) => {
      const el = document.getElementById(id)
      if (!el) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isScrolling.current) {
            setActiveSection(id)
          }
        },
        { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
      )

      observer.observe(el)
      observers.push(observer)
    })

    return () => observers.forEach((observer) => observer.disconnect())
  }, [])

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, sectionId: string) => {
    e.preventDefault()
    haptic("light")
    setActiveSection(sectionId)
    isScrolling.current = true
    
    // Smooth scroll bittikten sonra tekrar gözlemlemeye başla (Yaklaşık 1.3sn)
    setTimeout(() => {
      isScrolling.current = false
    }, 1300)

    const target = `#${sectionId}`
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lenis = (window as any).__lenisInstance

    if (lenis && typeof lenis.scrollTo === 'function') {
      lenis.scrollTo(target, { offset: -80, duration: 1.2 })
    } else {
      const element = document.getElementById(sectionId)
      if (element) {
        const top = element.getBoundingClientRect().top + window.scrollY - 80
        window.scrollTo({ top, behavior: "smooth" })
      }
    }
  }

  return (
    <>
      {/* Üst Navbar (Desktop ve Mobil Logosu için) */}
      <nav
        role="navigation"
        aria-label="Ana navigasyon"
        className={`fixed inset-x-0 z-50 top-0`}
      >
        <div
          className={`border-b transition-[border-color,box-shadow,background-color] duration-300 ${
            isScrolled
              ? "border-border/80 shadow-sm navbar-blur-scrolled"
              : "border-transparent navbar-blur-top"
          }`}
        >
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
            <Link href="/" className="flex min-w-0 items-center gap-3">
              <div className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md bg-secondary/10 ring-1 ring-border/60">
                <Image src="/logo.png" alt="FiveLine logo" fill className="object-cover" />
              </div>
              <div className="min-w-0">
                <span className="block truncate font-[family-name:var(--font-heading)] text-base font-bold leading-none text-foreground">
                  FiveLine Development
                </span>
                <span className="hidden text-[12px] font-semibold uppercase tracking-[0.12em] text-muted-foreground sm:block">
                  Software Studio
                </span>
              </div>
            </Link>

            {/* Desktop Navigasyon */}
            <div className="hidden h-full items-center gap-7 md:flex">
              {navLinks.map((link) => {
                const isActive = activeSection === link.href.replace("#", "")

                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href.replace("#", ""))}
                    className={`relative flex h-full items-center text-sm font-medium transition-colors ${
                      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span>{link.label}</span>
                    {isActive && (
                      <motion.span
                        layoutId="nav-active"
                        className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-primary"
                        transition={{ duration: 0.25 }}
                      />
                    )}
                  </Link>
                )
              })}
            </div>

            <div className="hidden items-center gap-3 md:flex">
              <Link href="mailto:info@fiveline.com.tr" className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground">
                info@fiveline.com.tr
              </Link>
              <Button
                asChild
                className="h-10 rounded-md bg-foreground px-4 text-background shadow-none hover:bg-primary hover:text-primary-foreground"
              >
                <Link href="#iletisim" onClick={(e) => scrollToSection(e, 'iletisim')}>Projeni Başlat</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobil Menü Arka Plan Karartması */}
      <AnimatePresence>
        {isDrawerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsDrawerOpen(false)}
            className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          />
        )}
      </AnimatePresence>

      {/* Mobil Alt Menü (Premium Floating Pill) */}
      <div 
        className={`md:hidden fixed left-0 right-0 z-50 flex justify-center px-4 pointer-events-none transition-all duration-500 ease-out ${isAtBottom ? "translate-y-[150%] opacity-0" : "translate-y-0 opacity-100"}`}
        style={{ bottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        <motion.div 
          layout
          className="pointer-events-auto flex flex-col rounded-[2rem] border border-white/10 bg-background/60 backdrop-blur-3xl p-2 shadow-[0_8px_32px_rgba(0,0,0,0.12)] ring-1 ring-white/5 saturate-[1.5]"
        >
          <AnimatePresence>
            {isDrawerOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                drag="y"
                dragConstraints={{ top: 0, bottom: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, { offset, velocity }) => {
                  if (offset.y > 50 || velocity.y > 500) {
                    setIsDrawerOpen(false)
                  }
                }}
                className="overflow-hidden w-full min-w-[260px] touch-none"
              >
                <div className="flex flex-col pt-2 pb-2 px-1">
                  <div className="w-12 h-1.5 bg-white/20 rounded-full mx-auto mb-3 cursor-grab active:cursor-grabbing" />
                  {navLinks.map((link) => {
                    const Icon = link.iconName ? iconMap[link.iconName] : null
                    return (
                      <Link
                        key={link.href}
                        href={link.href}
                        onClick={(e) => {
                          scrollToSection(e, link.href.replace("#", ""))
                          setIsDrawerOpen(false)
                        }}
                        className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-foreground/80 transition-colors hover:bg-white/5 active:bg-white/10 active:text-brand"
                      >
                        {Icon && <Icon className="w-4 h-4 shrink-0 text-foreground/50" />}
                        {link.label}
                      </Link>
                    )
                  })}
                  <div className="h-px bg-white/10 my-2 mx-2" />
                  <Button 
                    onClick={(e) => {
                      scrollToSection(e, 'iletisim')
                      setIsDrawerOpen(false)
                    }}
                    className="mx-1 h-11 rounded-2xl bg-brand text-white hover:bg-brand/90 shadow-lg shadow-brand/20 mb-1"
                  >
                    Teklif İste
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center gap-1 w-full justify-between">
            <Link href="#hero" onClick={(e) => scrollToSection(e, 'hero')} className={`relative flex items-center justify-center rounded-full h-12 px-3.5 transition-colors hover:text-foreground active:scale-95 ${activeSection === 'hero' ? 'text-foreground' : 'text-foreground/70'}`}>
              {activeSection === 'hero' && (
                <motion.div layoutId="bottom-nav-indicator" className="absolute inset-0 rounded-full bg-brand/15" transition={{ type: "spring", stiffness: 400, damping: 30 }} />
              )}
              <div className="relative z-10 flex items-center">
                <Home className={`w-5 h-5 shrink-0 transition-colors ${activeSection === 'hero' ? 'text-brand' : ''}`} />
                <span className={`overflow-hidden whitespace-nowrap text-sm font-semibold text-brand transition-all duration-300 ease-in-out ${activeSection === 'hero' ? 'max-w-[100px] opacity-100 ml-2' : 'max-w-0 opacity-0 ml-0'}`}>
                  Ana Sayfa
                </span>
              </div>
            </Link>

            <Link href="#hizmetler" onClick={(e) => scrollToSection(e, 'hizmetler')} className={`relative flex items-center justify-center rounded-full h-12 px-3.5 transition-colors hover:text-foreground active:scale-95 ${activeSection === 'hizmetler' ? 'text-foreground' : 'text-foreground/70'}`}>
              {activeSection === 'hizmetler' && (
                <motion.div layoutId="bottom-nav-indicator" className="absolute inset-0 rounded-full bg-brand/15" transition={{ type: "spring", stiffness: 400, damping: 30 }} />
              )}
              <div className="relative z-10 flex items-center">
                <Layers className={`w-5 h-5 shrink-0 transition-colors ${activeSection === 'hizmetler' ? 'text-brand' : ''}`} />
                <span className={`overflow-hidden whitespace-nowrap text-sm font-semibold text-brand transition-all duration-300 ease-in-out ${activeSection === 'hizmetler' ? 'max-w-[100px] opacity-100 ml-2' : 'max-w-0 opacity-0 ml-0'}`}>
                  Hizmetler
                </span>
              </div>
            </Link>

            <Link href="#hesaplayici" onClick={(e) => scrollToSection(e, 'hesaplayici')} className={`relative flex items-center justify-center rounded-full h-12 px-3.5 transition-colors hover:text-foreground active:scale-95 ${activeSection === 'hesaplayici' ? 'text-foreground' : 'text-foreground/70'}`}>
              {activeSection === 'hesaplayici' && (
                <motion.div layoutId="bottom-nav-indicator" className="absolute inset-0 rounded-full bg-brand/15" transition={{ type: "spring", stiffness: 400, damping: 30 }} />
              )}
              <div className="relative z-10 flex items-center">
                <Calculator className={`w-5 h-5 shrink-0 transition-colors ${activeSection === 'hesaplayici' ? 'text-brand' : ''}`} />
                <span className={`overflow-hidden whitespace-nowrap text-sm font-semibold text-brand transition-all duration-300 ease-in-out ${activeSection === 'hesaplayici' ? 'max-w-[100px] opacity-100 ml-2' : 'max-w-0 opacity-0 ml-0'}`}>
                  Hesapla
                </span>
              </div>
            </Link>

            <div className="w-[1px] h-6 bg-border/50 mx-1" />

            <button 
              onClick={() => {
                haptic("medium")
                setIsDrawerOpen(!isDrawerOpen)
              }}
              className="relative flex items-center justify-center rounded-full w-12 h-12 px-0 text-foreground/70 transition-colors hover:text-foreground active:scale-95"
            >
              <motion.div
                initial={false}
                animate={{ rotate: isDrawerOpen ? 90 : 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              >
                {isDrawerOpen ? <X className="w-5 h-5 shrink-0" /> : <Menu className="w-5 h-5 shrink-0" />}
              </motion.div>
            </button>
          </div>
        </motion.div>
      </div>
    </>
  )
}
